/**
 * useBookingEligibility — may this person pay, and if not, what is missing?
 *
 * WHY THIS EXISTS. Until now the answer lived in exactly one place: a private boolean inside
 * `create-payment`, evaluated after the guest had picked dates, chosen delivery points, accepted
 * the terms and typed a full card number — and after `ensureBooking()` had already inserted a
 * `pending_payment` rental. The refusal carried no machine-readable code, so the client could
 * not attach copy to it, and the checkout fell back to its generic string: "Pago rechazado.
 * Intentá de nuevo o usá otro método de pago." A person who had simply never verified was told
 * their CARD was declined. They blamed their bank, tried a second card, and left.
 *
 * NOT A FOURTH RE-DERIVATION. This reads the server eligibility predicate — profile-wide for a
 * general gate, or exact vehicle/window for checkout — which is the same rental-aware predicate
 * `create-payment` consults. The app already carries several independent restatements of "is
 * this person verified", and their disagreements are precisely how a screen ends up inviting a
 * payment the server will refuse. The server stays the authority; this hook only asks it early.
 *
 * FAILS OPEN. On a network error or a cold cache, `resolved` stays false and callers leave the
 * checkout exactly as it was. Telling a fully verified person they cannot pay is a worse failure
 * than letting them tap a button the server will answer for — and the server answers either way.
 */
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { verificationService, type BookingEligibilityScope } from '../services/verification.service'
import type { BookingBlocker, BookingEligibility } from '../types/verification.types'

export interface UseBookingEligibilityResult {
  /** True once we have a definitive answer. Never gate anything while false. */
  resolved: boolean
  /** Only meaningful once `resolved`. */
  allowed: boolean
  /** Unmet conditions, in the order the server listed them. */
  missing: BookingBlocker[]
  /** Blocked, but this build has no copy for the reason. Say so; never render "nothing". */
  hasUnknownBlocker: boolean
  isLoading: boolean
}

export const bookingEligibilityKey = (
  profileId: string,
  requiresAml?: boolean | null,
  scope?: BookingEligibilityScope,
) =>
  [
    'booking-eligibility',
    profileId,
    requiresAml ?? 'unknown',
    scope?.vehicleId ?? null,
    scope?.startAt ?? null,
    scope?.endAt ?? null,
  ] as const

/**
 * @param requiresAml Whether the in-flight booking carries platform coverage
 *   (the premium that owes a Qualitas declaration). `null`/undefined means the
 *   vehicle has not resolved it: the server default (declaration required)
 *   applies until the caller learns the truth and this query refetches. Callers
 *   with the vehicle in hand MUST pass `vehicleHasPlatformCoverage(vehicle)`.
 * @param scope Exact vehicle and rental window when the caller has them. The
 *   server may honor a one-time administrative deferral only with all three;
 *   a scoped request waits for all three instead of briefly rendering a
 *   profile-wide refusal while the checkout is still hydrating.
 */
export function useBookingEligibility(
  requiresAml?: boolean | null,
  scope?: BookingEligibilityScope,
): UseBookingEligibilityResult {
  const { profile, isLoading: authLoading } = useAuth()
  const profileId = profile?.id ?? ''
  const requestedScopedEligibility = scope !== undefined
  const hasCompleteScope = Boolean(scope?.vehicleId && scope.startAt && scope.endAt)

  const query = useQuery<BookingEligibility>({
    queryKey: bookingEligibilityKey(profileId, requiresAml, scope),
    queryFn: () => verificationService.getBookingEligibility(requiresAml ?? undefined, scope),
    enabled: !authLoading && !!profileId && (!requestedScopedEligibility || hasCompleteScope),
    // Short, because this is the screen a person lands on straight after finishing the step
    // that was blocking them. A stale "te falta la licencia" after they just uploaded one is
    // the same lie in the other direction.
    staleTime: 10_000,
    // An operator can grant the one-time, exact-window exception while this
    // checkout is already open. Recheck only scoped checkout queries so the
    // guest is not stranded behind a stale ordinary denial; generic detail-page
    // gates keep their existing non-polling behavior.
    refetchInterval: requestedScopedEligibility ? 10_000 : false,
    refetchIntervalInBackground: false,
  })

  // `isSuccess`, never `!isLoading`: an errored query also stops loading, and treating a failed
  // fetch as "not eligible" would block a verified guest on a dropped connection.
  const resolved = !authLoading && !!profileId && query.isSuccess

  return {
    resolved,
    allowed: query.data?.allowed === true,
    missing: query.data?.missing ?? [],
    hasUnknownBlocker: query.data?.hasUnknownBlocker === true,
    isLoading: authLoading || query.isLoading,
  }
}
